<script setup lang="ts">
import { effectiveAllergenText } from "~/utils/effectiveAllergens";

interface Props {
  options: Record<string, unknown>[];
  showTitle?: boolean;
  limit?: number;
  choices?: number;
  showPrice?: boolean;
  showSelectors?: boolean;
  minQuantity?: number;
  disableOperators?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showTitle: false,
  limit: 1,
  choices: 100,
  showPrice: true,
  showSelectors: true,
  minQuantity: 1,
  disableOperators: false,
});

const emit = defineEmits<{
  returnOption: [options: Record<string, unknown>[]];
}>();

const selectedOptions = ref<Record<string, unknown>[]>([]);
const selectedList = ref<Record<string, unknown>[]>([]);

const updatedLimit = computed(() => {
  return (
    props.limit -
    selectedList.value.reduce(
      (acc: number, item: Record<string, unknown>) => acc + Number(item.quantity || 0),
      0,
    )
  );
});

onMounted(() => {
  if (props.limit === 1) {
    selectedOptions.value = props.options.filter(
      (option: Record<string, unknown>) => option.quantity && Number(option.quantity) > 0,
    );
  } else {
    selectedList.value = props.options.filter(
      (option: Record<string, unknown>) => option.quantity && Number(option.quantity) > 0,
    );
  }
});

function isOptionSelected(optionId: unknown): boolean {
  return selectedList.value.some(
    (item: Record<string, unknown>) => item.id === optionId && Number(item.quantity) > 0,
  );
}

function handleCheckboxChange(option: Record<string, unknown>) {
  const isSelected = isOptionSelected(option.id);
  const newQuantity = isSelected ? 0 : 1;
  updateList({
    product: {
      ...option,
      quantity: newQuantity,
    },
  });
}

function updateList({ product }: { product: Record<string, unknown> }) {
  const list = selectedList.value
    .filter((item: Record<string, unknown>) => item.id !== product.id)
    .concat(Number(product.quantity) > 0 ? [product] : []);

  selectedList.value = list;
  emit("returnOption", list);
}

function updateOption(option: Record<string, unknown>) {
  selectedOptions.value = [{ ...option, quantity: 1 }];
  emit("returnOption", selectedOptions.value);
}
</script>

<template>
  <div class="option-list">
    <div
      v-for="(option, i) in options"
      :key="`modifier_${i}`"
      class="option-list__wrapper"
      :class="{
        unselected:
          ((limit > choices
            ? updatedLimit === 0
            : selectedList.filter((item) => Number(item.quantity) > 0).length >= choices) &&
            !selectedList.find(
              (item) => item.id === option.id && Number(item.quantity) > 0,
            )) ||
          (selectedOptions.length > 0 &&
            !selectedOptions.find(
              (item) => item.id === option.id && Number(item.quantity) > 0,
            )),
      }"
    >
      <div class="option-list__wrapper--text">
        <p class="grey-text">
          {{ option.name }}
        </p>
        <p
          v-show="option.description"
          class="small-text light-grey-text"
          v-html="
            String(option.description || '').replace(/<\/?[^>]+(>|$)/g, '')
          "
        />
        <div
          v-if="option.allergens"
          class="allergens"
        >
          <p
            v-if="(option.allergens as Record<string, unknown>[]).length > 0"
            class="light-grey-text small-text"
          >
            Allergens: {{ effectiveAllergenText(option) }}
          </p>
          <ProductAllergens :product="option" />
        </div>
      </div>
      <p
        v-if="Number(option.price_gross) > 0 && showPrice"
        class="option-list__wrapper--price grey-text heavy"
      >
        +£{{ parseFloat(String(Number(option.price_gross) / 100)).toFixed(2) }}
      </p>
      <div
        v-if="showSelectors"
        class="option-list__wrapper--operators"
      >
        <input
          v-if="limit === 1"
          :checked="
            selectedOptions.some(
              (item) => item.id === option.id && Number(item.quantity) > 0,
            )
          "
          class="radio clear"
          :class="{
            fill: selectedOptions.some(
              (item) => item.id === option.id && Number(item.quantity) > 0,
            ),
          }"
          type="radio"
          @change="updateOption(option)"
        />
        <QuantityOperator
          v-else-if="limit > choices"
          :item="option"
          :product-quantity="
            selectedList.find((item) => item.id === option.id)?.quantity
              ? Number(
                  selectedList.find((item) => item.id === option.id)?.quantity,
                )
              : 0
          "
          :disabled="
            disableOperators ||
            (selectedList.filter((item) => Number(item.quantity) > 0).length >=
              choices &&
              !selectedList.find(
                (item) => item.id === option.id && Number(item.quantity) > 0,
              ))
          "
          :min-quantity="minQuantity"
          :picker="true"
          :max-quantity="updatedLimit"
          @return-quantity="updateList"
        />
        <input
          v-else
          :checked="isOptionSelected(option.id)"
          class="checkbox clear"
          :class="{
            fill: isOptionSelected(option.id),
          }"
          :disabled="
            disableOperators ||
            (selectedList.filter((item) => Number(item.quantity) > 0).length >=
              choices &&
              !selectedList.find(
                (item) => item.id === option.id && Number(item.quantity) > 0,
              ))
          "
          type="checkbox"
          @change="handleCheckboxChange(option)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.option-list {
  display: block;
  width: 100%;
  margin: 0 0 0.5rem;
  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 10px 0;
    border-bottom: 2px solid var(--color-grey-3);
    &:last-child {
      border-bottom: none;
    }
    &--price {
      display: flex;
    }
    &--image {
      width: 15%;
      border-radius: var(--radius);
    }
    &--text {
      width: 75%;
      .quantity {
        color: var(--color-grey-1) !important;
        font-weight: var(--weight-semibold);
      }
      .allergens {
        .product-allergens {
          p {
            margin: 0 !important;
          }
        }
      }
    }
    &--allergens {
      p {
        margin: 0 !important;
      }
    }
    &--operators {
      .radio,
      .checkbox {
        width: 1rem;
        height: 1rem;
        border: 2px solid var(--color-grey-3);
        cursor: pointer;
      }
      .radio {
        border-radius: 50%;
      }
      .checkbox {
        border-radius: 0.3rem;
      }
      .fill {
        background-color: var(--color-pink-1);
        border: 2px solid var(--color-pink-1);
      }
    }
  }
  .unselected {
    opacity: 0.5;
  }
}
</style>
